/**
 * Workspace-subdomain routing.
 *
 * After Auth0 login (or a workspace switch), the backend JWT carries a
 * `workspace_slug` claim (see aimsos-platform's create_token/switch_workspace
 * handlers). This module turns that slug into the {slug}.{rootDomain} URL
 * the browser should be on, so the active workspace is identifiable from
 * the URL alone and switching organizations is a real navigation.
 *
 * Crossing to that URL carries OUR OWN JWT via a one-time handoff fragment
 * (see tokenHandoff.js) rather than re-running Auth0 on the destination
 * origin — Auth0 is only ever invoked once, for identity.
 *
 * No test runner is wired up for this package yet (see packages/auth's
 * package.json — no `test` script), so `resolveWorkspaceSubdomainUrl` is
 * deliberately a pure function (no window/DOM access) taking explicit
 * location pieces, making it trivial to unit test once that's set up.
 */

import { attachHandoff } from './tokenHandoff.js';

/**
 * Root domain the app's workspace subdomains hang off of in this
 * environment, e.g. "acme.com" in prod/staging. Empty string when unset
 * (local dev without a real domain).
 *
 * @returns {string}
 */
export function getRootDomain() {
  return (import.meta.env.VITE_APP_ROOT_DOMAIN || '').trim();
}

/**
 * Build the {slug}.{rootDomain} URL to redirect the browser to after login
 * or a workspace switch, preserving the given path (pathname + search +
 * hash). Returns `null` when no redirect is needed or possible:
 *
 *   - `slug` is missing (e.g. an older workspace row not yet backfilled)
 *   - the browser is already on that workspace's subdomain
 *
 * When `rootDomain` is not configured (local dev), this falls back to the
 * "*.localhost" convention (e.g. `acme-corp.localhost:5173`), which modern
 * browsers resolve to loopback with no `/etc/hosts` changes required.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.slug - target workspace slug
 * @param {string} params.hostname - current window.location.hostname
 * @param {string} params.protocol - current window.location.protocol (e.g. "https:")
 * @param {string} [params.port] - current window.location.port (may be "")
 * @param {string} [params.path] - pathname+search+hash to preserve (defaults to "/")
 * @param {string} [params.rootDomain] - defaults to getRootDomain()
 * @returns {string|null}
 */
export function resolveWorkspaceSubdomainUrl({
  slug,
  hostname,
  protocol,
  port = '',
  path = '/',
  rootDomain = getRootDomain(),
}) {
  if (!slug) return null;

  const currentFirstLabel = (hostname || '').split('.')[0];
  if (currentFirstLabel === slug) return null;

  const targetHost = rootDomain ? `${slug}.${rootDomain}` : `${slug}.localhost`;
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${targetHost}${portSuffix}${path}`;
}

/**
 * Extract the workspace slug from the CURRENT hostname, e.g.
 * "acme-corp.acme.com" -> "acme-corp", or "acme-corp.localhost" ->
 * "acme-corp" in dev. Returns `undefined` on the bare root domain (no
 * subdomain) or an unrelated host.
 *
 * Used by WorkspaceGate (apps/core-shell) to compare "where the browser
 * currently is" against the active JWT's `workspace_slug` claim, to decide
 * whether a handoff redirect (or the workspace picker) is needed.
 *
 * @param {string} [hostname] - defaults to window.location.hostname
 * @param {string} [rootDomain] - defaults to getRootDomain()
 * @returns {string|undefined}
 */
export function getCurrentSlugFromHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  rootDomain = getRootDomain(),
) {
  if (!hostname) return undefined;

  if (rootDomain) {
    if (hostname === rootDomain || !hostname.endsWith(`.${rootDomain}`)) return undefined;
    return hostname.slice(0, hostname.length - rootDomain.length - 1) || undefined;
  }

  // Dev fallback: {slug}.localhost (bare "localhost"/"127.0.0.1" has no slug).
  if (!hostname.endsWith('.localhost')) return undefined;
  return hostname.slice(0, hostname.length - '.localhost'.length) || undefined;
}

/**
 * Convenience wrapper around `resolveWorkspaceSubdomainUrl` that reads the
 * current location from `window`, attaches a one-time JWT handoff fragment
 * (see tokenHandoff.js) so the destination origin lands already
 * authenticated — no second Auth0 round-trip — and performs the redirect
 * via `window.location.href`. No-op (returns `false`) when no redirect is
 * needed (missing slug, or already on that subdomain).
 *
 * @param {string|null|undefined} slug
 * @param {{ token: string, profile?: object }} handoff - our own backend JWT
 *   (+ optional display profile) to carry to the destination origin.
 * @param {{ path?: string }} [options]
 * @returns {boolean} true when a redirect was triggered
 */
export function redirectToWorkspaceSubdomainWithHandoff(slug, handoff, { path } = {}) {
  if (typeof window === 'undefined') return false;
  const { hostname, protocol, port, pathname, search } = window.location;
  const url = resolveWorkspaceSubdomainUrl({
    slug,
    hostname,
    protocol,
    port,
    path: path ?? `${pathname}${search}`,
  });
  if (!url) return false;
  window.location.href = attachHandoff(url, handoff);
  return true;
}
